import React, { useEffect, useState } from 'react';
import { Button, Space, Modal } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { EnvironmentOutlined, PlusCircleOutlined, FolderOpenOutlined, FolderAddOutlined } from '@ant-design/icons';
import '../components/MapView.scss';
import FileUpload from './FileUpload';
import DataPortalStep from '../MapViewStepComponents/DataPortalStep';
import AddFromDataPortal from '../MapViewStepComponents/AddFromDataPortal';
import AddDataToNewFolder from '../MapViewStepComponents/AddDataToNewFolder';
import ServiceURL from '../MapViewStepComponents/ServiceURL';
import UploadProgressModal from '../components/UploadProgressModal';
import { useLegendForm, useLegendUI } from '../../../context/MapLegendContext';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { useMapPortalFileUpload } from '../../../hooks/useMapPortalFileUpload';
import { useCustomFileMap } from '../../../hooks/useCustomFileMap';
import { FileTypes } from '../../../enums/fileTypes';
import { UploadFlows } from '../../../enums/uploadFlows';
import { UploadDestination } from '../../../enums/uploadDestination';
import { NotificationTypes } from '../../../enums/notificationTypes';
import Notification from '../../../shared/components/Notification';
import { FileNode } from '../../../types/FileManagerTypes';
import { fileTypeMap, ROOT_DISPLAY_NAME } from '../../FileManager/constants';

interface MapUploadFlowProps {
  isDataPortalModalVisible: boolean;
  setIsDataPortalModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * The map's "Upload Data" stepper (data portal, upload new to a new or existing
 * folder, WMS URL, progress). A finished upload opens Layer Properties through
 * LegendUIContext. Shared by Map Portal's map view and CommonMap.
 */
const MapUploadFlow: React.FC<MapUploadFlowProps> = ({ isDataPortalModalVisible, setIsDataPortalModalVisible }) => {
  const [isAddFromWMSUrl, setIsAddFromWMSUrl] = useState<boolean>(false);
  const [isUploadNewVisible, setIsUploadNewVisible] = useState<boolean>(false);
  const [isAddFromDataVisible, setIsAddFromDataVisible] = useState<boolean>(false);
  const [isAddFromToNewFolderVisible, setIsAddFromToNewFolderVisible] = useState<boolean>(false);
  const [isAddToExistingFolderVisible, setIsAddToExistingFolderVisible] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isVFSVisible, setIsVFSVisible] = useState<boolean>(false);
  const [folderName, setFolderName] = useState<string>('');
  // Captured for the upcoming upload permissions flow; not sent yet.
  const [permissionValues, setPermissionValues] = useState<string[]>([]);
  const [uploadFlow, setUploadFlow] = useState<UploadFlows>();
  const [portalFiles, setPortalFiles] = useState<FileNode[]>([]);

  const { setModalVisible, setLayerUploadFlow, registerUploadAnotherHandler } = useLegendUI();
  const {
    uploadFileType,
    setUploadFileType,
    setUploadedFiles,
    setS3UniqueFolderName,
    setDatasetId,
    folderId,
    setFolderId,
    folderLocation,
    setFolderLocation,
    folderS3Key,
    setFolderS3Key,
  } = useLegendForm();
  const { setState: setLayerProperties } = useLayerProperitesContext();
  const { fileMap, rootFolderId, isMappingLoading, updateMapping, createFolder } = useCustomFileMap({});

  // Root is the default destination until the user picks a folder; derived so a
  // root id that never resolves stays unselected instead of standing in as one.
  const destinationFolderId = folderId || rootFolderId;
  const destinationS3Key = folderS3Key || rootFolderId;
  const destinationLabel = folderLocation || (rootFolderId ? ROOT_DISPLAY_NAME : '');

  const {
    handleFileChange,
    selectedFiles,
    uploadProgress,
    isUploading,
    setSelectedFiles,
    presignedFileUpload,
    cancelUpload,
    reserveUpload,
    releaseIfReserved,
  } = useMapPortalFileUpload(destinationFolderId, updateMapping);

  const [allowedExtensions, setAllowedExtensions] = useState<string[]>(fileTypeMap[FileTypes.SHAPEFILE]);

  const isFilesValid = (files: Array<{ name: string }> = portalFiles) => {
    if (!files || files.length === 0) return false;
    if (!allowedExtensions || allowedExtensions.length === 0) return true;

    const extensionOf = (name: string) => {
      const lowerName = name.toLowerCase();
      const index = lowerName.lastIndexOf('.');
      return index === -1 ? '' : lowerName.slice(index);
    };
    const baseOf = (name: string) => {
      const lowerName = name.toLowerCase();
      const index = lowerName.lastIndexOf('.');
      return index === -1 ? lowerName : lowerName.slice(0, index);
    };
    const isShapefile = allowedExtensions.some((ext) => ext.toLowerCase() === '.shp');

    if (isShapefile) {
      if (files.length !== 4) return false;

      const uploadedExtensions = files.map((file) => extensionOf(file.name));
      const expectedExtensions = allowedExtensions.map((ext) => ext.toLowerCase());
      if (new Set(uploadedExtensions).size !== expectedExtensions.length || !expectedExtensions.every((ext) => uploadedExtensions.includes(ext))) {
        return false;
      }

      const bases = files.map((file) => baseOf(file.name));
      return bases.every((base) => base === bases[0]);
    }

    return files.length === 1 && allowedExtensions.some((ext) => extensionOf(files[0].name) === ext.toLowerCase());
  };

  const handleFileTypeChange = React.useCallback((value: FileTypes) => {
    setUploadFileType(value);
    setAllowedExtensions(fileTypeMap[value]);
    setSelectedFiles([]);
  }, []);

  useEffect(() => {
    if (selectedFiles) setUploadedFiles(selectedFiles);
  }, [selectedFiles, setUploadedFiles]);

  const openLayerProperties = (flow: UploadFlows, dataFolderId: string) => {
    setLayerProperties((prev) => ({ ...prev, folderId: dataFolderId }));
    setLayerUploadFlow(flow);
    setModalVisible(true);
  };

  const resetUploadState = React.useCallback(() => {
    setFolderLocation('');
    setFolderId('');
    setFolderS3Key('');
    setFolderName('');
    setPermissionValues([]);
    setUploadFileType(FileTypes.SHAPEFILE);
    setAllowedExtensions(fileTypeMap[FileTypes.SHAPEFILE]);
    setCurrentStep(0);
    setSelectedFiles([]);
    setPortalFiles([]);
    setUploadedFiles([]); // RESET CONTEXT FILES
    // Stale value would point the next layer's data at the previous upload's folder.
    setS3UniqueFolderName('');
    // Same for the dataset: an abandoned flow must not carry its ID into the next save.
    setDatasetId(undefined);
  }, [setFolderLocation, setFolderId, setFolderS3Key, setUploadFileType, setSelectedFiles, setUploadedFiles, setS3UniqueFolderName, setDatasetId]);

  // Invoked by "Save and Upload Another" once the layer has saved.
  const handleUploadAnother = React.useCallback(() => {
    resetUploadState();
    setUploadFlow(UploadFlows.UPLOAD_NEW);
    setIsUploadNewVisible(true);
  }, [resetUploadState]);

  useEffect(() => registerUploadAnotherHandler(handleUploadAnother), [registerUploadAnotherHandler, handleUploadAnother]);

  const handleUploadStepsContinue = () => {
    if (currentStep === 1 && !isFilesValid(selectedFiles)) {
      Notification({
        message:
          uploadFileType === FileTypes.SHAPEFILE
            ? 'Select exactly one complete Shapefile set with matching filenames (.shp, .shx, .dbf, .prj).'
            : 'Select exactly one valid vector file.',
        type: NotificationTypes.ERROR,
      });
      return;
    }

    if (uploadFileType === FileTypes.WMS) {
      setIsAddFromWMSUrl(true);
      setIsAddFromDataVisible(false);
      setIsAddToExistingFolderVisible(false);
    }
    if (currentStep < uploadNewSteps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleDataPortalCancel();
      setIsAddFromDataVisible(false);
      // save-layer reads the destination back off the context, so the resolved
      // root default has to be committed before the upload starts.
      setFolderId(destinationFolderId);
      setFolderS3Key(destinationS3Key);
      presignedFileUpload(
        destinationFolderId,
        (value) => setS3UniqueFolderName(value),
        false,
        () => {
          setIsAddToExistingFolderVisible(false);
          openLayerProperties(UploadFlows.UPLOAD_NEW, destinationFolderId);
        },
        UploadDestination.EXISTING,
        destinationS3Key,
      );
    }
  };

  const handleDataPortalCancel = () => {
    setIsDataPortalModalVisible(false);
  };

  const handleAddDataExistingFolder = async () => {
    if (currentStep < addToNewFolder.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleDataPortalCancel();
      setIsAddFromDataVisible(false);
      setIsAddToExistingFolderVisible(false);
      const trimmedFolderName = folderName.trim();
      if (!trimmedFolderName) return;

      // Claim the upload guard before awaiting, so a second click on a slow
      // network can't create a duplicate folder.
      if (!reserveUpload()) return;
      try {
        const folder = await createFolder(trimmedFolderName, destinationFolderId);
        if (!folder?.folder_id) return;

        // Chain convention is [rootId, ...ancestorIds]; save-layer reads this
        // back off the context, so the new folder's own key has to land there.
        const createdFolderS3Key = folder.s3key ?? folder.s3_key ?? `${destinationS3Key}/${folder.folder_id}`;
        setFolderId(folder.folder_id);
        setFolderS3Key(createdFolderS3Key);
        setFolderLocation(folder.folder_name ?? trimmedFolderName);
        setS3UniqueFolderName(folder.folder_id);
        presignedFileUpload(
          folder.folder_id,
          (value) => setS3UniqueFolderName(value),
          false,
          () => {
            setIsAddFromToNewFolderVisible(false);
            openLayerProperties(UploadFlows.UPLOAD_NEW, folder.folder_id);
          },
          UploadDestination.EXISTING,
          createdFolderS3Key,
        );
      } finally {
        releaseIfReserved();
      }
    }
  };

  const handleBackWithinSteps = () => {
    if (currentStep > 0) {
      if (currentStep === 1) {
        setSelectedFiles([]); // leaving the dragger step resets its selection
      }
      setCurrentStep((step) => step - 1);
    }
  };

  const handleBackToUploadData = () => {
    setIsAddFromDataVisible(false);
    setIsUploadNewVisible(false);
    setIsDataPortalModalVisible(true);
    setCurrentStep(0);
  };

  const handleBackFromWms = () => {
    setIsAddFromWMSUrl(false);
    if (uploadFlow === UploadFlows.UPLOAD_NEW) {
      setIsAddToExistingFolderVisible(true);
    } else {
      setIsAddFromDataVisible(true);
    }
    setCurrentStep(0);
  };

  const handleFileRemove = React.useCallback(
    (file: UploadFile<any>) => {
      setSelectedFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    [setSelectedFiles],
  );

  const uploadNewSteps = [
    <DataPortalStep
      handleFileTypeChange={handleFileTypeChange}
      fileMap={fileMap}
      selectedFiles={portalFiles}
      setSelectedFiles={setPortalFiles}
      showFiles={false}
      destinationFolderId={destinationFolderId}
      destinationLabel={destinationLabel}
      isFolderMappingLoading={isMappingLoading}
    />,
    <FileUpload
      folderId={destinationFolderId}
      handleFileChange={handleFileChange}
      selectedFiles={selectedFiles}
      allowedExtensions={allowedExtensions}
      onRemove={handleFileRemove}
    />,
  ];

  const addToNewFolder = [
    <AddDataToNewFolder
      isVFSVisible={isVFSVisible}
      setIsVFSVisible={setIsVFSVisible}
      folderName={folderName}
      setFolderName={setFolderName}
      handleFileTypeChange={handleFileTypeChange}
      permissionValues={permissionValues}
      setPermissionValues={setPermissionValues}
      destinationFolderId={destinationFolderId}
      destinationLabel={destinationLabel}
      isFolderMappingLoading={isMappingLoading}
    />,
    <FileUpload
      folderId={destinationFolderId}
      handleFileChange={handleFileChange}
      selectedFiles={selectedFiles}
      allowedExtensions={allowedExtensions}
      onRemove={handleFileRemove}
    />,
  ];

  const addFromWMSURL = [
    <ServiceURL folderId={destinationFolderId} handleFileTypeChange={handleFileTypeChange} callback={() => setIsAddFromWMSUrl(false)} />,
  ];

  return (
    <>
      {/* Upload new */}
      <Modal
        title="UPLOAD NEW"
        open={isUploadNewVisible}
        onCancel={() => setIsUploadNewVisible(false)}
        footer={<Button onClick={handleBackToUploadData}>BACK</Button>}
        centered
        className="custom-modal"
      >
        <div className="custom-modal-body">
          <Button
            className="modal-option-button"
            onClick={() => {
              setIsUploadNewVisible(false);
              setIsAddFromToNewFolderVisible(true);
              resetUploadState();
            }}
            icon={<FolderAddOutlined className="modal-icon" />}
          >
            ADD TO A NEW FOLDER
          </Button>
          <Button
            className="modal-option-button"
            onClick={() => {
              setIsAddToExistingFolderVisible(true);
              setIsUploadNewVisible(false);
              resetUploadState();
            }}
            icon={<FolderOpenOutlined className="modal-icon" />}
          >
            ADD TO AN EXISTING FOLDER
          </Button>
        </div>
      </Modal>

      <AddFromDataPortal open={isAddFromDataVisible} onClose={() => setIsAddFromDataVisible(false)} onBack={handleBackToUploadData} />

      <Modal title="Upload Data" open={isDataPortalModalVisible} onCancel={handleDataPortalCancel} footer={null} centered className="custom-modal">
        <div className="custom-modal-body">
          <Button
            className="modal-option-button"
            onClick={() => {
              setIsAddFromDataVisible(true);
              setIsDataPortalModalVisible(false);
              setUploadFlow(UploadFlows.DATA_PORTAL);
              resetUploadState();
            }}
          >
            <Space>
              <EnvironmentOutlined className="modal-icon" />
              Add from data portal
            </Space>
          </Button>
          <Button
            className="modal-option-button"
            onClick={() => {
              setIsDataPortalModalVisible(false);
              setIsUploadNewVisible(true);
              setUploadFlow(UploadFlows.UPLOAD_NEW);
            }}
          >
            <Space>
              <PlusCircleOutlined className="modal-icon" />
              Upload New
            </Space>
          </Button>
        </div>
      </Modal>

      {/* Add to existing folder */}
      <Modal
        title="ADD DATA TO EXISTING FOLDER"
        open={isAddToExistingFolderVisible}
        onCancel={() => {
          setIsAddToExistingFolderVisible(false);
          resetUploadState();
        }}
        footer={null}
        centered
        width={600}
      >
        {uploadNewSteps[currentStep]}
        <div className="modal-footer">
          <Button
            onClick={
              currentStep > 0
                ? handleBackWithinSteps
                : () => {
                    setIsAddToExistingFolderVisible(false);
                    setIsUploadNewVisible(true);
                    setCurrentStep(0);
                  }
            }
          >
            BACK
          </Button>
          <Button
            className="mapview__btn-ml"
            type="primary"
            onClick={handleUploadStepsContinue}
            disabled={(currentStep === 0 && !destinationFolderId) || (currentStep === 1 && !isFilesValid(selectedFiles)) || isUploading}
          >
            {currentStep === uploadNewSteps.length - 1 ? 'FINISH' : 'CONTINUE'}
          </Button>
        </div>
      </Modal>

      {/* ADD DATA TO A NEW FOLDER */}
      <Modal
        title="ADD DATA TO NEW FOLDER"
        open={isAddFromToNewFolderVisible}
        onCancel={() => {
          setIsAddFromToNewFolderVisible(false);
          resetUploadState();
        }}
        footer={null}
        centered
        width={600}
      >
        {addToNewFolder[currentStep]}
        <div className="modal-footer">
          <Button
            onClick={
              currentStep > 0
                ? handleBackWithinSteps
                : () => {
                    setIsAddFromToNewFolderVisible(false);
                    setIsUploadNewVisible(true);
                    setCurrentStep(0);
                  }
            }
          >
            BACK
          </Button>
          <Button
            className="mapview__btn-ml"
            type="primary"
            onClick={handleAddDataExistingFolder}
            disabled={
              (currentStep === 0 && (!destinationFolderId || !folderName)) || (currentStep === 1 && !isFilesValid(selectedFiles)) || isUploading
            }
          >
            {currentStep === addToNewFolder.length - 1 ? 'FINISH' : 'CONTINUE'}
          </Button>
        </div>
      </Modal>

      {/* Add WMS URL */}
      <Modal title="ADD LAYER FROM WMS URL" open={isAddFromWMSUrl} onCancel={() => setIsAddFromWMSUrl(false)} footer={null} centered width={600}>
        {addFromWMSURL[0]}
        <div className="modal-footer">
          <Button onClick={handleBackFromWms}>BACK</Button>
          <Button
            className="mapview__btn-ml"
            type="primary"
            onClick={handleAddDataExistingFolder}
            disabled={currentStep !== addFromWMSURL.length - 1}
          >
            {currentStep === addFromWMSURL.length ? 'FINISH' : 'CONTINUE'}
          </Button>
        </div>
      </Modal>

      <UploadProgressModal uploadProgress={uploadProgress} onCancel={cancelUpload} />
    </>
  );
};

export default MapUploadFlow;
